/**
 * THE CONSOLE'S OWN ZOOM, 80–200 % (Hamzah, 2026-10-26: "the zoom in out functionality too"),
 * independent of the browser's zoom, the OS's scaling and the display.
 *
 * Ported from the FESCO prototype's `Zoom` module, which is where the approach was proven:
 * CSS `zoom` on <html> scales every element in proportion — type, tables, cards, forms, charts,
 * the rail — and three things are kept consistent while the level is not 100 %, so the console
 * behaves the way the browser's own zoom does:
 *
 *   1. VIEWPORT UNITS. `100vh` inside a zoomed document is one screen tall times the zoom, so a
 *      `max-height: calc(100vh - 32px)` dialog would run off the bottom. Every stylesheet rule
 *      that uses vh/vw/dvh is rewritten to divide by `--app-zoom` (the inline ones in our own
 *      components say `/ var(--app-zoom, 1)` themselves). `--app-zoom` is unset at 100 %, so the
 *      fallback 1 makes those expressions exactly what they were.
 *   2. MEDIA QUERIES on width/height are rescaled, so a layout reflows to the viewport the person
 *      actually sees: at 200 % on a 1600 px window the console lays out as at 800 px.
 *   3. GEOMETRY. Chromium and Firefox report `getBoundingClientRect` and pointer coordinates in
 *      ZOOMED pixels while a `left: 120px` is laid out in unzoomed ones, which would put every
 *      popover, tooltip, drag handle, chart crosshair, SLD node pick and Twin pick in the wrong
 *      place. The rects, the pointer coordinates, `innerWidth/Height`, `elementFromPoint` and
 *      `matchMedia` are divided back into the document's own pixels, and `devicePixelRatio` is
 *      multiplied so a canvas (Mapbox, the Twin, Highcharts' boost) draws at the sharper density.
 *      A probe decides whether the engine needs it at all; Safari's legacy zoom does not.
 *
 * NOTHING IS PATCHED AT 100 %. The geometry patches divide by `k()`, which is 1 until a level
 * other than 100 is applied, and the stylesheet walk only runs the first time one is. A person who
 * never touches the zoom runs exactly the code they ran before this file existed.
 *
 * Signed in only: `Shell` applies the level on mount and resets it on unmount, so the sign-in
 * screens always render at 100 %, as the prototype's do.
 */

const VIEWPORT_UNIT = /(-?\d*\.?\d+)([dsl]?v(?:h|w|min|max))\b/g;
const PX = /(\d*\.?\d+)px/g;

let z = 1;
/** Whether this engine reports geometry in zoomed pixels. Null until the first probe. */
let scaled: boolean | null = null;
let prepared = false;
const walked = new WeakSet<CSSStyleSheet>();
const mediaRules: Array<[CSSMediaRule, string]> = [];

/** The divisor the geometry patches use: 1 at 100 % and on an engine that needs no help. */
const k = (): number => (z !== 1 && scaled === true ? z : 1);

function scaleMedia(text: string): string {
  return z === 1 ? text : text.replace(PX, (_m, n: string) => `${Number(n) * z}px`);
}

function walkRules(rules: CSSRuleList): void {
  for (const r of Array.from(rules)) {
    if (r instanceof CSSMediaRule) {
      if (/(min|max)-(width|height)/.test(r.media.mediaText)) {
        const text = r.media.mediaText;
        mediaRules.push([r, text]);
        try {
          r.media.mediaText = scaleMedia(text);
        } catch {
          /* a rule the engine will not let us rewrite keeps its original query */
        }
      }
      walkRules(r.cssRules);
      continue;
    }
    if ('style' in r && r.style instanceof CSSStyleDeclaration) {
      const style = r.style;
      for (let i = 0; i < style.length; i += 1) {
        const prop = style.item(i);
        const value = style.getPropertyValue(prop);
        VIEWPORT_UNIT.lastIndex = 0;
        if (value === '' || value.includes('--app-zoom') || !VIEWPORT_UNIT.test(value)) continue;
        VIEWPORT_UNIT.lastIndex = 0;
        style.setProperty(prop, value.replace(VIEWPORT_UNIT, 'calc($1$2 / var(--app-zoom, 1))'), style.getPropertyPriority(prop));
      }
    }
    if ('cssRules' in r && r.cssRules instanceof CSSRuleList) walkRules(r.cssRules);
  }
}

function walkSheet(sheet: CSSStyleSheet): void {
  if (walked.has(sheet)) return;
  let rules: CSSRuleList;
  try {
    rules = sheet.cssRules;
  } catch {
    return; /* a cross-origin sheet (a web font's) cannot be read and holds no layout */
  }
  walked.add(sheet);
  walkRules(rules);
}

function walkAll(): void {
  for (const sheet of Array.from(document.styleSheets)) walkSheet(sheet);
}

/** A browser that refuses one patch must never break the console. */
function safe(fn: () => void): void {
  try {
    fn();
  } catch {
    /* leave that one API unpatched */
  }
}

function patchGetter(proto: object, props: readonly string[], scale: (v: number) => number): void {
  for (const p of props) {
    safe(() => {
      const d = Object.getOwnPropertyDescriptor(proto, p);
      if (!d?.get || !d.configurable) return;
      const get = d.get;
      Object.defineProperty(proto, p, {
        configurable: true,
        enumerable: d.enumerable ?? true,
        get(this: unknown) {
          return scale(get.call(this) as number);
        },
      });
    });
  }
}

function prepare(): void {
  if (prepared) return;
  prepared = true;
  walkAll();
  /* Code-split chunks bring their own stylesheets later (the Overview's, the SLD's): walk each
     as it arrives, so a screen opened after the zoom was set is rescaled like the first one. */
  safe(() => {
    new MutationObserver((records) => {
      for (const rec of records) {
        for (const node of Array.from(rec.addedNodes)) {
          if (node instanceof HTMLStyleElement && node.sheet) walkSheet(node.sheet);
          if (node instanceof HTMLLinkElement && node.rel === 'stylesheet') {
            if (node.sheet) walkSheet(node.sheet);
            else node.addEventListener('load', () => node.sheet && walkSheet(node.sheet), { once: true });
          }
        }
      }
    }).observe(document.head, { childList: true });
  });

  const div = (r: DOMRect): DOMRect => {
    const s = k();
    return s === 1 ? r : new DOMRect(r.x / s, r.y / s, r.width / s, r.height / s);
  };
  const gbcr = Element.prototype.getBoundingClientRect;
  const gcr = Element.prototype.getClientRects;
  safe(() => {
    Element.prototype.getBoundingClientRect = function getBoundingClientRect(this: Element): DOMRect {
      return div(gbcr.call(this));
    };
  });
  safe(() => {
    Element.prototype.getClientRects = function getClientRects(this: Element): DOMRectList {
      const list = gcr.call(this);
      if (k() === 1) return list;
      const rects = Array.from(list).map(div);
      return Object.assign(rects, { item: (i: number) => rects[i] ?? null }) as unknown as DOMRectList;
    };
  });
  safe(() => {
    const rb = Range.prototype.getBoundingClientRect;
    Range.prototype.getBoundingClientRect = function getBoundingClientRect(this: Range): DOMRect {
      return div(rb.call(this));
    };
  });
  /* offsetX/Y too: Highcharts and Mapbox both hit-test with them on some paths. */
  patchGetter(MouseEvent.prototype, ['clientX', 'clientY', 'pageX', 'pageY', 'x', 'y', 'offsetX', 'offsetY'], (v) => v / k());
  if (typeof Touch !== 'undefined') patchGetter(Touch.prototype, ['clientX', 'clientY', 'pageX', 'pageY'], (v) => v / k());
  for (const p of ['innerWidth', 'innerHeight'] as const) {
    safe(() => {
      const d = Object.getOwnPropertyDescriptor(window, p);
      if (!d?.get || !d.configurable) return;
      const get = d.get;
      const setter = d.set;
      Object.defineProperty(window, p, {
        configurable: true,
        enumerable: true,
        get: () => (get.call(window) as number) / k(),
        set: (v: number) => setter?.call(window, v),
      });
    });
  }
  safe(() => {
    const d = Object.getOwnPropertyDescriptor(window, 'devicePixelRatio');
    if (!d?.get || !d.configurable) return;
    const get = d.get;
    Object.defineProperty(window, 'devicePixelRatio', {
      configurable: true,
      enumerable: true,
      get: () => (get.call(window) as number) * k(),
    });
  });
  safe(() => {
    const efp = document.elementFromPoint.bind(document);
    const efps = document.elementsFromPoint.bind(document);
    document.elementFromPoint = (x: number, y: number) => efp(x * k(), y * k());
    document.elementsFromPoint = (x: number, y: number) => efps(x * k(), y * k());
  });
  safe(() => {
    const mm = window.matchMedia.bind(window);
    window.matchMedia = (q: string) => mm(k() === 1 ? q : q.replace(PX, (_m, n: string) => `${Number(n) * z}px`));
  });

  /* The probe: does this engine report geometry in zoomed pixels? (Chromium and Firefox's
     standard zoom do; Safari's legacy zoom does not.) Measured with the ORIGINAL rect reader. */
  safe(() => {
    const root = document.documentElement;
    const before = root.style.zoom;
    root.style.zoom = '2';
    const p = document.createElement('div');
    p.style.cssText = 'position:fixed;left:0;top:0;width:100px;height:10px;visibility:hidden;pointer-events:none';
    document.body.append(p);
    scaled = gbcr.call(p).width > 150;
    p.remove();
    root.style.zoom = before;
  });
}

export function zoomSupported(): boolean {
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('zoom', '1.5');
}

/**
 * Apply a level to the whole document, 80–200. 100 removes every trace: no `zoom`, no
 * `--app-zoom`, every media query back to its own text.
 */
export function applyZoom(pct: number): void {
  const level = zoomSupported() ? Math.min(200, Math.max(80, Math.round(pct))) : 100;
  /* Already at 100 and asked for 100: touch nothing, not even a resize event. */
  if (level === 100 && z === 1) return;
  if (level !== 100) prepare();
  z = level / 100;
  const root = document.documentElement;
  if (z === 1) {
    root.style.removeProperty('--app-zoom');
    root.style.zoom = '';
    delete root.dataset.appZoom;
  } else {
    root.style.setProperty('--app-zoom', String(z));
    root.style.zoom = String(z);
    root.dataset.appZoom = String(level);
  }
  for (const [rule, text] of mediaRules) {
    try {
      rule.media.mediaText = scaleMedia(text);
    } catch {
      /* keeps whichever query it had */
    }
  }
  /* Charts, the map and the Twin size themselves on resize; tell them the viewport moved. */
  window.dispatchEvent(new Event('resize'));
}
